import megaphoneIcon from '@tagvenue/icons/24/megaphone.svg?raw';
import megaphoneDotIcon from '@tagvenue/icons/24/megaphone-dot.svg?raw';

import { deleteChildNodes } from '@/modules/misc/common/navbar/utils/deleteChildNodes';
import { csrfToken } from '@/utils';

let activeController = null;

const CLASSES = {
  container: 'ds-header__announcements',
  button: 'ds-header__announcements__button',
  buttonActive: 'ds-header__announcements__button--active',
  panel: 'ds-header__announcements__panel',
  panelOpen: 'ds-header__announcements__panel--open',
  panelHeader: 'ds-header__announcements__panel-header',
  card: 'ds-header__announcements__card',
  cardText: 'ds-header__announcements__card-text',
  cardTitle: 'ds-header__announcements__card-title',
  cardDescription: 'ds-header__announcements__card-description',
  cardDate: 'ds-header__announcements__card-date',
  cardDot: 'ds-header__announcements__card-dot',
  cardDotHidden: 'ds-header__announcements__card-dot--hidden',
  panelEmpty: 'ds-header__announcements__panel-empty',
  panelEmptyIcon: 'ds-header__announcements__panel-empty-icon',
  panelEmptyTitle: 'ds-header__announcements__panel-empty-title',
  panelEmptySubtitle: 'ds-header__announcements__panel-empty-subtitle',
};

function markAnnouncementsAsRead(announcementIds) {
  fetch('/venue-cabinet/announcements/ajax/mark-read', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken(),
    },
    body: JSON.stringify({ announcementIds }),
  }).catch((error) => {
    console.error('Failed to mark announcements as read', error);
  });
}

function createCard(announcement, onRead) {
  const card = document.createElement('div');
  card.classList.add(CLASSES.card);

  const textWrapper = document.createElement('div');
  textWrapper.classList.add(CLASSES.cardText);

  const title = document.createElement('div');
  title.classList.add(CLASSES.cardTitle);
  title.textContent = announcement.title;

  const description = document.createElement('div');
  description.classList.add(CLASSES.cardDescription);
  description.textContent = announcement.caption;

  textWrapper.appendChild(title);
  textWrapper.appendChild(description);

  const dot = document.createElement('span');
  dot.classList.add(CLASSES.cardDot);
  if (announcement.isRead) {
    dot.classList.add(CLASSES.cardDotHidden);
  }

  card.appendChild(textWrapper);
  card.appendChild(dot);

  card.addEventListener('click', () => {
    onRead(announcement);
    dot.classList.add(CLASSES.cardDotHidden);
    const destination = announcement.url || '/venue-cabinet/announcements';
    window.open(destination, '_blank', 'noopener,noreferrer');
  });

  return card;
}

function createPanel(announcements, onRead) {
  const panel = document.createElement('div');
  panel.classList.add(CLASSES.panel);

  if (announcements.length === 0) {
    const empty = document.createElement('div');
    empty.classList.add(CLASSES.panelEmpty);
    const icon = document.createElement('div');
    icon.classList.add(CLASSES.panelEmptyIcon);
    icon.innerHTML = '<svg fill="none" height="32" viewBox="0 0 24 24" width="32" xmlns="http://www.w3.org/2000/svg"><g clip-rule="evenodd" fill="currentColor" fill-rule="evenodd"><path d="m22 6.99999c.5523 0 1 .44772 1 1v4.00001c0 .5523-.4477 1-1 1s-1-.4477-1-1v-4.00001c0-.55228.4477-1 1-1z"/><path d="m16.5669 2.14128c.2924-.13193.7182-.30185 1.1762-.24575.5733.07021 1.0475.35181 1.3835.82153.264.369.3218.80683.3469 1.12256.0265.33372.0265.75875.0265 1.2465v9.82768c0 .4878 0 .9128-.0265 1.2466-.0251.3157-.0829.7535-.3469 1.1225-.336.4697-.8102.7513-1.3835.8215-.458.0561-.8838-.1138-1.1762-.2457-.3176-.1433-.71-.3572-1.1667-.6062l-.0345-.0189c-1.5687-.8551-3.4096-1.6423-4.8657-1.7262 0 1.0948-.0004 2.1897 0 3.2845.0001.1903.0002.3539-.0144.5021-.1403 1.4244-1.26721 2.5513-2.69155 2.6915-.15899.0157-.33568.0145-.54405.0145-.20836 0-.38505.0012-.54405-.0145-1.42433-.1402-2.55122-1.2671-2.6915-2.6915-.0146-.1482-.01453-.3118-.01446-.5021.00044-1.1014.00001-2.2028.00001-3.3042-.05593-.0025-.11006-.0055-.16238-.0091-.32983-.0225-.65974-.072-.98567-.207-.73508-.3045-1.3191-.8885-1.62358-1.6236-.13501-.3259-.18451-.6558-.20702-.9856-.021372-.3133-.021361-.6918-.02135-1.1304v-1.2733c-.000013-.80498-.000024-1.46934.04419-2.01054.04593-.56209.14449-1.0788.39178-1.56413.3835-.75265.99542-1.36457 1.74807-1.74807.48533-.24729 1.00204-.34585 1.56413-.39178.5412-.04422 1.20556-.0442 2.01053-.04419h3.4913c1.4911 0 3.4558-.82881 5.1157-1.73366l.0345-.01886c.4568-.24898.8491-.46287 1.1667-.60619z"/></g></svg>';
    const emptyTitle = document.createElement('div');
    emptyTitle.classList.add(CLASSES.panelEmptyTitle);
    emptyTitle.textContent = 'You are all set';
    const emptySubtitle = document.createElement('div');
    emptySubtitle.classList.add(CLASSES.panelEmptySubtitle);
    emptySubtitle.textContent = 'Keep an eye on new announcements here!';
    empty.appendChild(icon);
    empty.appendChild(emptyTitle);
    empty.appendChild(emptySubtitle);
    panel.appendChild(empty);
    return panel;
  }

  const header = document.createElement('span');
  header.classList.add(CLASSES.panelHeader);
  header.textContent = 'Tagvenue announcements';
  panel.appendChild(header);

  for (const announcement of announcements) {
    const group = document.createElement('div');

    const date = document.createElement('div');
    date.classList.add(CLASSES.cardDate);
    date.textContent = announcement.date;

    group.appendChild(date);
    group.appendChild(createCard(announcement, onRead));
    panel.appendChild(group);
  }

  return panel;
}

function setButtonIcon(button, hasUnread) {
  button.innerHTML = hasUnread ? megaphoneDotIcon : megaphoneIcon;

  const svg = button.querySelector('svg');
  if (svg) {
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
  }
}

function createButton(hasUnread) {
  const button = document.createElement('button');
  button.setAttribute('aria-label', 'Announcements');
  button.classList.add(CLASSES.button);
  setButtonIcon(button, hasUnread);

  return { button };
}

export function createVCAnnouncementsButton() {
  const wrapper = document.getElementById('announcements-wrapper');

  if (!wrapper) {
    return;
  }

  const announcements = window.__VC_ANNOUNCEMENTS ?? [];

  if (activeController) {
    activeController.abort();
  }
  activeController = new AbortController();
  const { signal } = activeController;

  deleteChildNodes(wrapper);

  const hasUnread = announcements.some(a => !a.isRead);

  const { button } = createButton(hasUnread);

  function updateButtonDot() {
    const anyUnread = announcements.some(a => !a.isRead);
    setButtonIcon(button, anyUnread);
  }

  function onRead(announcement) {
    App.track.event('navbar_interaction', {
      section: 'navbar_manager',
      element: 'announcements_modal',
      element_value: announcement.trackingElementValue || announcement.id,
      interaction: 'click',
    });

    announcement.isRead = true;
    markAnnouncementsAsRead([announcement.id]);
    updateButtonDot();
  }

  const container = document.createElement('div');
  container.classList.add(CLASSES.container);

  let isOpen = false;
  let outsideClickHandler = null;
  let panel;

  function closePanel() {
    panel.classList.remove(CLASSES.panelOpen);
    button.classList.remove(CLASSES.buttonActive);
    isOpen = false;

    if (outsideClickHandler) {
      document.removeEventListener('mousedown', outsideClickHandler);
      outsideClickHandler = null;
    }
  }

  function openPanel() {
    panel.classList.add(CLASSES.panelOpen);
    button.classList.add(CLASSES.buttonActive);
    isOpen = true;

    outsideClickHandler = (e) => {
      if (!container.contains(e.target)) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', outsideClickHandler);
  }

  panel = createPanel(announcements, onRead);

  button.addEventListener('mouseenter', () => {
    App.track.event('navbar_interaction', {
      section: 'navbar_manager',
      element: 'announcements_button',
      element_value: '',
      interaction: 'hover',
    });
  });

  button.addEventListener('click', () => {
    App.track.event('navbar_interaction', {
      section: 'navbar_manager',
      element: 'announcements_button',
      element_value: '',
      interaction: 'click',
    });

    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  });

  container.appendChild(button);
  container.appendChild(panel);

  const desktopQuery = window.matchMedia('(min-width: 1280px)');

  desktopQuery.addEventListener('change', (e) => {
    if (e.matches) {
      deleteChildNodes(wrapper);
      wrapper.appendChild(container);
    } else {
      if (isOpen) {
        closePanel();
      }
      container.remove();
    }
  }, { signal });

  if (desktopQuery.matches) {
    wrapper.appendChild(container);
  }
}
